import { memo, useMemo } from "react"

import { useHumanFeedbackUpdate } from "@/human-feedback/annotations"
import { NumericalAnnotationInput } from "@/metrics/metric-display"
import {
  FIELD_CONTAINER_STYLES,
  KnowledgeBaseDisableControl,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  LLMModelSelect,
  QuestionTextarea,
  TargetFilterToggle,
  TEXT_INPUT_STYLES,
  TranscriptFilterDetails,
  TranscriptFilterToggle,
} from "@/metrics/registry/configuration"
import {
  supportsModelSelection,
  supportsTranscriptFilter,
  type TestResult,
} from "@/metrics/runtime"
import { useUser } from "@clerk/nextjs"

import { logger } from "@/lib/logger"
import type { Annotation } from "@/types/api/Annotation"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { GetCompletionMutation } from "@/utils/backend-mimics/mimicLLMBinaryManager"
import { mimicNumericalLLMJudge } from "@/utils/backend-mimics/mimicNumericalLLMJudgeManager"
import { replaceWithAttributes } from "@/utils/backend-mimics/replace_with_attributes"
import type { SimulationWithContext } from "@/utils/backend-mimics/SimulationWithContext"
import {
  getMetricExecutionConfigDiff,
  getMetricExplanation,
  isMetricExecutionConfigCurrent,
  type MetricOutputBoundarySource,
} from "@/utils/metric-output-boundaries"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getMetricQuestionSection } from "../helpers/getMetricQuestionSection"
import { NumericalHumanFeedbackButton } from "../helpers/NumericalHumanFeedbackButton"

const log = logger("NumericalLLMMetric")

interface NumericalHumanReviewInputWrapperProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  projectId?: string
  minValue: number
  maxValue: number
}

const NumericalHumanReviewInputWrapper: React.FC<NumericalHumanReviewInputWrapperProps> =
  memo(
    ({
      metricId,
      simulationOutputId,
      annotationsForMetric,
      projectId,
      minValue,
      maxValue,
    }) => {
      const { user } = useUser()

      const myAnnotation = useMemo(() => {
        return annotationsForMetric?.find(
          (annotation: Annotation) =>
            annotation.assignee === user?.emailAddresses?.[0]?.emailAddress,
        )
      }, [annotationsForMetric, user?.emailAddresses])

      const { currentAnnotation, handleValueUpdate } = useHumanFeedbackUpdate({
        simulationOutputId,
        metricId,
        initialAnnotation: myAnnotation || null,
        projectId,
      })

      return (
        <NumericalAnnotationInput
          currentValue={currentAnnotation?.ground_truth_float_value ?? null}
          onValueChange={(value, explanation, comment) => {
            void handleValueUpdate(value, explanation, comment)
          }}
          metricId={metricId}
          minValue={minValue}
          maxValue={maxValue}
        />
      )
    },
  )
NumericalHumanReviewInputWrapper.displayName =
  "NumericalHumanReviewInputWrapper"

export class NumericalLLMMetricRecord extends NumericalMetricRecord {
  overviewSize = "medium" as const

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(
    metricOutput: MetricOutputBoundarySource,
  ): boolean {
    return isMetricExecutionConfigCurrent(
      this.metric_metadata?.question,
      metricOutput,
      this,
    )
  }

  getVersionDiff(
    metricOutput: MetricOutputBoundarySource,
  ): Array<{ field: string; current: string; previous: string }> {
    const diff = getMetricExecutionConfigDiff(
      this.metric_metadata?.question,
      metricOutput,
      this,
    )
    return diff ? [diff] : []
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const explanationText = getMetricExplanation(mo)

      return (
        <div className="space-y-4">
          {explanationText && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                Explanation
              </h3>
              <div className="text-sm whitespace-pre-wrap wrap-break-word bg-content1/50 dark:bg-content1/50 p-4 rounded-lg">
                {explanationText}
              </div>
            </div>
          )}
        </div>
      )
    } catch (error) {
      log.error("Error parsing metadata field explanation", error)
      return (
        <div className="text-sm text-foreground">
          <strong>Result</strong>: {mo.float_value ?? "N/A"}
        </div>
      )
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    const showTranscriptFilter = supportsTranscriptFilter(metricRecordData)
    const showModelSelection = supportsModelSelection(metricRecordData)

    return (
      <div className="space-y-6">
        <QuestionTextarea
          metricRecordData={metricRecordData}
          onChange={onChange}
          label="Metric Prompt"
          required
          placeholder="Enter a prompt that asks the LLM to rate something on a numerical scale.

Example: 'On a scale of 1-10, how professionally did the agent handle the customer's complaint?'

The LLM will return a number within your specified min/max range based on analyzing the conversation."
        />

        <div className="grid grid-cols-2 gap-4">
          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>Min Value</label>
            </div>
            <input
              type="number"
              className={TEXT_INPUT_STYLES}
              value={metricRecordData.metric_metadata?.min_value ?? ""}
              onChange={(e) => {
                onChange({
                  ...metricRecordData,
                  metric_metadata: {
                    ...metricRecordData.metric_metadata,
                    min_value: Number(e.target.value),
                  },
                })
              }}
              placeholder="Enter minimum value"
            />
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>Max Value</label>
            </div>
            <input
              type="number"
              className={TEXT_INPUT_STYLES}
              value={metricRecordData.metric_metadata?.max_value ?? ""}
              onChange={(e) => {
                onChange({
                  ...metricRecordData,
                  metric_metadata: {
                    ...metricRecordData.metric_metadata,
                    max_value: Number(e.target.value),
                  },
                })
              }}
              placeholder="Enter maximum value"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <KnowledgeBaseDisableControl
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
          {showModelSelection && (
            <LLMModelSelect
              metricRecordData={metricRecordData}
              onChange={onChange}
            />
          )}
          {showTranscriptFilter && (
            <TranscriptFilterToggle
              metricRecordData={metricRecordData}
              onChange={onChange}
            />
          )}
        </div>
        {showTranscriptFilter && (
          <TranscriptFilterDetails
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
        )}
        <TargetFilterToggle
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
      </div>
    )
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    return (
      <div className="flex flex-col gap-2">
        <div>
          <h3 className="font-semibold">Question: </h3>
          <p>{metricRecordData.metric_metadata.question}</p>
        </div>
        <div>
          <h3 className="font-semibold">Min Value: </h3>
          <p>{metricRecordData.metric_metadata.min_value}</p>
        </div>
        <div>
          <h3 className="font-semibold">Max Value: </h3>
          <p>{metricRecordData.metric_metadata.max_value}</p>
        </div>
        {metricRecordData.metric_metadata.use_knowledge_base !== undefined && (
          <div>
            <h3 className="font-semibold">Knowledge Base: </h3>
            <p>
              {metricRecordData.metric_metadata.use_knowledge_base
                ? "Enabled"
                : "Disabled"}
            </p>
          </div>
        )}
      </div>
    )
  }

  renderMetricType(): React.ReactNode {
    return "Numerical LLM Judge"
  }

  getDeepDiveSections(
    mo: RunMetricOutput,
  ): { label: string; content: React.ReactNode }[] | null {
    try {
      const explanationText = getMetricExplanation(mo)
      const sections: { label: string; content: React.ReactNode }[] = []

      const questionSection = getMetricQuestionSection(
        mo,
        this.metric_metadata?.question,
      )
      if (questionSection) {
        sections.push(questionSection)
      }

      if (explanationText) {
        sections.push({
          label: "Explanation",
          content: (
            <div className="text-sm whitespace-pre-wrap wrap-break-word">
              {explanationText}
            </div>
          ),
        })
      }

      return sections.length > 0 ? sections : null
    } catch {
      return null
    }
  }

  hasCustomVotingUI = true

  renderHumanFeedbackValueButton(
    annotation: Annotation | null,
    onValueChange: (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
    ) => void,
    onExplanationChange: (explanation: string | null) => void,
    otherUserAnnotations?: Annotation[],
  ): React.ReactNode {
    const minValue = this.metric_metadata?.min_value ?? 1
    const maxValue = this.metric_metadata?.max_value ?? 10

    return (
      <NumericalHumanFeedbackButton
        annotation={annotation}
        onValueChange={onValueChange}
        onExplanationChange={onExplanationChange}
        otherUserAnnotations={otherUserAnnotations}
        minValue={minValue}
        maxValue={maxValue}
      />
    )
  }

  getKeyboardShortcutHints(): { key: string; label: string }[] {
    return [
      { key: "0-9", label: "Enter value" },
      { key: "Enter", label: "Confirm" },
    ]
  }

  renderAssignmentsTableReviewCell(
    simulationOutputId: string,
    annotations_for_metric: Annotation[],
    projectId?: string,
  ): React.ReactNode {
    const minValue = this.metric_metadata?.min_value ?? 1
    const maxValue = this.metric_metadata?.max_value ?? 10

    return (
      <NumericalHumanReviewInputWrapper
        metricId={this.id || ""}
        simulationOutputId={simulationOutputId}
        annotationsForMetric={annotations_for_metric}
        projectId={projectId}
        minValue={minValue}
        maxValue={maxValue}
      />
    )
  }

  async evaluateTranscript(
    simulationWithContext: SimulationWithContext,
    getCompletionMutation: GetCompletionMutation,
  ): Promise<TestResult> {
    const question = this.metric_metadata?.question || ""
    const replacedQuestion = replaceWithAttributes(
      question,
      simulationWithContext,
    )
    const minValue = this.metric_metadata?.min_value ?? 0
    const maxValue = this.metric_metadata?.max_value ?? 10

    try {
      const evaluationResult = await mimicNumericalLLMJudge(
        replacedQuestion,
        simulationWithContext.transcript,
        getCompletionMutation,
        minValue,
        maxValue,
      )

      if (!evaluationResult || typeof evaluationResult !== "object") {
        throw new Error("Invalid response format from LLM")
      }

      const { answer, explanation, decision_message_indices } = evaluationResult

      if (!explanation || !Array.isArray(decision_message_indices)) {
        throw new Error("Missing required fields in LLM response")
      }

      const stringAnswer =
        answer !== null && answer !== undefined ? answer.toString() : "Error"

      return {
        question,
        answer: stringAnswer,
        explanation,
        decision_message_indices,
        timestamp: Date.now(),
        transcriptId: simulationWithContext.simulation_output_id,
        rawResponse: evaluationResult,
      }
    } catch (error: unknown) {
      return {
        question,
        answer: "Error",
        explanation:
          (error as { message?: string })?.message ||
          "Failed to process response",
        decision_message_indices: [],
        timestamp: Date.now(),
        transcriptId: simulationWithContext.simulation_output_id,
        rawResponse: {
          error: (error as { message?: string })?.message || "Unknown error",
        },
      }
    }
  }
}
